import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  esquemaIngreso,
  traducirError,
  type DatosIngreso,
  type ReclamacionesSesion,
} from '@discopico/domain';
import { rutaInicialDe } from '../../nucleo/autorizacion/sesion.js';
import { ErrorAcceso, ingresar } from './api.js';

/**
 * `alEntrar` recibe la sesión y el destino ya resuelto. La navegación la hace la ruta y
 * no este componente, para que la prueba no tenga que montar un router entero para
 * verificar cuatro filas de un esquema de escenario.
 */
export type PropsPantallaIngreso = {
  alEntrar: (sesion: ReclamacionesSesion | null, destino: string) => void;
};

export function PantallaIngreso({ alEntrar }: PropsPantallaIngreso) {
  const [errorServidor, setErrorServidor] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DatosIngreso>({
    resolver: zodResolver(esquemaIngreso),
    defaultValues: { tipoDocumento: 'cc', numeroDocumento: '', password: '' },
  });

  async function enviar(datos: DatosIngreso) {
    setErrorServidor(null);
    try {
      const sesion = await ingresar(datos);
      // Con la contraseña temporal pendiente el destino es la pantalla de cambio, no la
      // vista del rol. Es el primero de los dos candados del escenario; el segundo está
      // en app.assert_puede_operar y no depende de esta línea.
      // El super administrador elige primero con qué rol entra, feature 36.
      const destino = sesion?.debeCambiarPassword
        ? '/cambiar-password'
        : sesion?.puedeElegirRol
          ? '/elegir-rol'
          : rutaInicialDe(sesion?.rol);
      alEntrar(sesion, destino);
    } catch (e) {
      // Se traduce el CÓDIGO, no se muestra `e.message`. El catálogo de `errores.ts` dice
      // que el código es el contrato entre la base y la interfaz y que el mensaje no lo
      // es, así que confiar en el mensaje sería confiar en que quien construyó el error
      // se acordó de traducirlo, y eso no lo obliga nada. Medido: con el plan tal cual,
      // el usuario veía `E_CREDENCIALES_INVALIDAS` en pantalla.
      setErrorServidor(traducirError(e instanceof ErrorAcceso ? e.codigo : 'E_DESCONOCIDO'));
    }
  }

  /** Campo y mensaje de error comparten forma para que los tres se vean iguales. */
  const campo = 'min-h-12 rounded-[10px] border border-tinta/20 bg-hundido px-3 text-[16px]';

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-7 p-6">
      <header className="grid justify-items-center gap-3">
        <img src="/isotipo.png" alt="" width={34} height={51} />
        <h1 className="font-titulo text-[28px] font-semibold leading-none">Discópico</h1>
        <p className="text-[13.5px] text-tinta-suave">Control financiero del gastrobar</p>
      </header>

      <form onSubmit={handleSubmit(enviar)} className="grid gap-4" noValidate>
        <div className="grid gap-1.5">
          <label htmlFor="tipoDocumento" className="text-[14.5px]">
            Tipo de documento
          </label>
          <select id="tipoDocumento" {...register('tipoDocumento')} className={campo}>
            <option value="cc">Cédula de ciudadanía</option>
            <option value="ce">Cédula de extranjería</option>
          </select>
        </div>

        <div className="grid gap-1.5">
          <label htmlFor="numeroDocumento" className="text-[14.5px]">
            Número de documento
          </label>
          <input
            id="numeroDocumento"
            {...register('numeroDocumento')}
            // inputMode text y no numeric: la cédula de extranjería lleva letras, y un
            // teclado numérico dejaría a la contadora sin poder escribir su documento.
            inputMode="text"
            autoComplete="username"
            autoCapitalize="characters"
            className={campo}
            aria-invalid={errors.numeroDocumento ? 'true' : undefined}
          />
          {errors.numeroDocumento && (
            <p className="text-[13px] text-alerta">{errors.numeroDocumento.message}</p>
          )}
        </div>

        <div className="grid gap-1.5">
          <label htmlFor="password" className="text-[14.5px]">
            Contraseña
          </label>
          <input
            id="password"
            type="password"
            {...register('password')}
            autoComplete="current-password"
            className={campo}
            aria-invalid={errors.password ? 'true' : undefined}
          />
          {errors.password && <p className="text-[13px] text-alerta">{errors.password.message}</p>}
        </div>

        {/* role alert para que el lector de pantalla lo anuncie sin que el foco se mueva.
            SUPERFICIE LLENA y no texto de color: es la regla del sistema para los bloqueos,
            porque en el bar hay luz roja y un texto rojo sobre marrón ahí no se ve. */}
        {errorServidor && (
          <p role="alert" className="rounded-[10px] bg-sup-bloqueo px-3 py-2.5 text-[14px]">
            {errorServidor}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-1 min-h-12 rounded-[14px] bg-coral px-5 font-titulo text-[17px] font-semibold text-sobre-coral disabled:opacity-70"
        >
          {isSubmitting ? 'Entrando...' : 'Entrar'}
        </button>
      </form>
    </main>
  );
}
